import { ApolloClient } from 'apollo-client'
import * as React from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import Loader from 'main/components/common/Loader'
import { AppContext } from 'main/framework'
import { CredentialsFields } from 'main/gql/credentials'
import { User_user } from 'main/gql/user'
import { Flags, useFeatureFlag } from 'main/hooks/useFeatureFlag'
import { searchPath } from 'main/routes'
import { login } from 'main/util/auth'
import { UnreachableCaseError } from 'main/util/ts'
import { UserSignUpSource, userType, UserType } from 'main/util/user'

import ChooseRole, { ChooseRoleSSO } from './ChooseRole'
import { DirectorSignUpForm, DirectorSignUpFormSSO } from './director'
import LoginForm, { LoginFormSSO } from './LoginForm'
import {
  FinalParentSignUpForm,
  InitialParentSignUpForm,
  InitialParentSignUpFormOnboarding,
  InitialParentSignUpFormSSO,
  IntermediateParentSignUpForm,
} from './parent'
import * as parent from './parent/util'
import { Action as TrackAction, ITrackData, track } from './track'
import { Step } from './types'

enum AuthAction {
  Login = 'login',
  SignUp = 'sign_up',
}

interface IProps extends RouteComponentProps {
  client: ApolloClient<{}>
  trackData: ITrackData
  user: User_user | null
  step: Step
  isOnboarding?: boolean
  onAuth: (credentials: CredentialsFields) => void
  onChangeStep: (step: Step) => void
  onForgotPassword: () => void
  onRequestClose: () => void
}

function Content(props: IProps) {
  const {
    history,
    client,
    trackData,
    user,
    step,
    isOnboarding,
    onAuth,
    onChangeStep,
    onForgotPassword,
    onRequestClose,
  } = props
  const ssoFlag = useFeatureFlag(Flags.SSO)
  const isSSO = ssoFlag.enabled()

  const appContext = React.useContext(AppContext)

  switch (step) {
    case 'login':
      return renderLogin()
    case 'choose_role':
      return renderChooseRole()
    case 'director':
      // NOTE (aboltunov): this is currently unreachable as `ChooseRole` was updated such that the
      // button to sign up as a director redirects to another page rather than continuing within
      // the modal.
      return renderDirectorSignUp()
    case 'parent':
      return renderInitialParentSignUpForm()
    case 'parent_onboarding':
      return (
        <InitialParentSignUpFormOnboarding
          onComplete={(credentials: CredentialsFields) => {
            handleAuthComplete(AuthAction.SignUp, credentials)
          }}
        />
      )
    case 'parent_intermediate':
    case 'parent_intermediate_existing':
      if (!user) return <Loader />
      return (
        <IntermediateParentSignUpForm
          user={user}
          isOnboarding={isOnboarding}
          onComplete={() => {
            track('completed_parent_sign_up_intermediate', trackData)

            if (parent.requiresFinalForm(user)) {
              onChangeStep('parent_final')
            } else {
              onRequestClose()
            }
          }}
        />
      )
    case 'parent_final':
      if (!user) return <Loader />
      // Note: prop displayPhoneField should be set to false for tour modal source, default is true - Cam
      return (
        <FinalParentSignUpForm
          isOnboarding={isOnboarding}
          displayPhoneField
          user={user}
          onComplete={() => {
            track('completed_parent_sign_up_final', trackData)
            onRequestClose()
            redirectToSearch()
          }}
        />
      )
    default:
      throw new UnreachableCaseError(step)
  }

  function redirectToSearch() {
    if (history.location.pathname === '/home' || history.location.pathname === '/search') {
      window.location.replace(searchPath())
    }
  }

  function handleAuthComplete(
    authAction: AuthAction,
    credentials: CredentialsFields,
    trackAction?: TrackAction,
  ) {
    login(client, credentials, appContext).then(() => {
      if (trackAction) track(trackAction, trackData)
      onAuth(credentials)
      advancePostAuth(props, credentials, authAction)
    })
  }

  function renderLogin() {
    const loginProps = {
      segmentData: { action: 'completed_login', data: trackData },
      trackingData: {},
    }
    if (isSSO) return <LoginFormSSO {...loginProps} />

    return (
      <LoginForm
        onComplete={(credentials: CredentialsFields) =>
          handleAuthComplete(AuthAction.Login, credentials, 'completed_login')
        }
        onForgotPassword={() => onForgotPassword()}
        onRequestSignUp={() => onChangeStep('choose_role')}
      />
    )
  }

  function renderChooseRole() {
    const chooseRoleProps = {
      segmentData: { data: trackData },
      source: UserSignUpSource.ChooseRoleModal,
      trackingData: {},
    }
    if (isSSO) return <ChooseRoleSSO {...chooseRoleProps} />

    return (
      <ChooseRole
        onRequestLogin={() => onChangeStep('login')}
        onRequestParentSignUp={() => onChangeStep('parent')}
      />
    )
  }

  function renderDirectorSignUp() {
    const directorSignUpProps = {
      segmentData: { event: 'completed_director_sign_up', data: trackData },
      source: UserSignUpSource.DirectorSignUpModal,
    }
    if (isSSO) return <DirectorSignUpFormSSO {...directorSignUpProps} />

    return (
      <DirectorSignUpForm
        onComplete={(credentials: CredentialsFields) =>
          handleAuthComplete(AuthAction.SignUp, credentials, 'completed_director_sign_up')
        }
        onRequestLogin={() => onChangeStep('login')}
        onRequestParentSignUp={() => onChangeStep('parent')}
      />
    )
  }

  function renderInitialParentSignUpForm() {
    const initialProps = {
      segmentData: { event: 'completed_parent_sign_up', data: trackData },
      source: UserSignUpSource.ParentSignUpModal,
      trackingData: {},
    }

    if (isSSO) return <InitialParentSignUpFormSSO {...initialProps} />

    return (
      <InitialParentSignUpForm
        {...initialProps}
        onComplete={credentials =>
          handleAuthComplete(AuthAction.SignUp, credentials, 'completed_parent_sign_up')
        }
        onRequestLogin={() => onChangeStep('login')}
        onRequestDirectorSignUp={() => history.push('/join')}
      />
    )
  }
}

export default withRouter(Content)

function advancePostAuth(props: IProps, credentials: CredentialsFields, action: AuthAction) {
  const type = userType(credentials.user)

  switch (type) {
    case UserType.Director:
      return advanceDirectorPostAuth(props, action)
    case UserType.Parent:
      return advanceParentPostAuth(props, credentials, action)
    default:
      throw new UnreachableCaseError(type)
  }
}

function advanceDirectorPostAuth(props: IProps, action: AuthAction) {
  const { onRequestClose } = props

  switch (action) {
    case AuthAction.Login:
      return onRequestClose()
    case AuthAction.SignUp:
      return window.location.assign('/setup-assistant')
    default:
      throw new UnreachableCaseError(action)
  }
}

function advanceParentPostAuth(props: IProps, credentials: CredentialsFields, action: AuthAction) {
  const { onChangeStep, onRequestClose } = props
  const { user } = credentials

  if (parent.requiresIntermediateForm(user)) {
    onChangeStep(
      action === AuthAction.Login ? 'parent_intermediate_existing' : 'parent_intermediate',
    )
  } else if (parent.requiresFinalForm(user)) {
    onChangeStep('parent_final')
  } else {
    onRequestClose()
  }
}
